import React, { useState, useEffect, useRef } from 'react';
import { evaluateStrength, checkPwnedCount, PASSWORD_MIN_LENGTH } from '../../utils/passwordStrength';

/**
 * Champ mot de passe avec :
 *  - bascule afficher/masquer
 *  - indicateur visuel de robustesse (Faible / Moyen / Fort / Excellent)
 *  - contrôle HIBP (k-anonymity) avec avertissement si compromis
 *  - validation longueur mini (NIST 2024)
 *
 * Remonte l'état de validité au parent via onValidityChange({ tooShort, pwned, pwnedCount, checking }).
 */
const PasswordStrengthField = ({
  value,
  onChange,
  label = 'Nouveau mot de passe',
  placeholder = '••••••••',
  testid = 'password-strength',
  onValidityChange,
  autoFocus = false,
  dark = false,
}) => {
  const [show, setShow] = useState(false);
  const [pwnedCount, setPwnedCount] = useState(0);
  const [checking, setChecking] = useState(false);
  const validityRef = useRef(onValidityChange);
  validityRef.current = onValidityChange;

  const strength = evaluateStrength(value);
  const len = (value || '').length;
  const tooShort = len > 0 && len < PASSWORD_MIN_LENGTH;

  // Contrôle HIBP débounce (500 ms) — seulement si longueur suffisante.
  useEffect(() => {
    if (!value || value.length < PASSWORD_MIN_LENGTH) {
      setPwnedCount(0);
      setChecking(false);
      return;
    }
    let active = true;
    setChecking(true);
    const t = setTimeout(async () => {
      const count = await checkPwnedCount(value);
      if (active) {
        setPwnedCount(count);
        setChecking(false);
      }
    }, 500);
    return () => { active = false; clearTimeout(t); };
  }, [value]);

  // Remontée de la validité au parent (sans inclure le callback dans les deps).
  useEffect(() => {
    validityRef.current?.({
      tooShort: !value || value.length < PASSWORD_MIN_LENGTH,
      pwned: pwnedCount > 0,
      pwnedCount,
      checking,
    });
  }, [value, pwnedCount, checking]);

  const labelCls = dark ? 'text-slate-200' : 'text-gray-700';
  const inputCls = dark
    ? 'w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-4 pr-12 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition'
    : 'w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]';

  return (
    <div data-testid={`${testid}-wrapper`}>
      <label className={`block text-sm font-medium mb-2 ${labelCls}`}>{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={inputCls}
          data-testid={testid}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((v) => !v)}
          aria-label={show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          className={`absolute inset-y-0 right-0 flex items-center pr-4 ${dark ? 'text-slate-300 hover:text-white' : 'text-gray-400 hover:text-gray-700'} transition`}
          data-testid={`${testid}-toggle`}
        >
          <i className={show ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
        </button>
      </div>

      {/* Barre de robustesse */}
      {len > 0 && (
        <div className="mt-2" data-testid={`${testid}-meter`}>
          <div className={`h-1.5 w-full rounded-full overflow-hidden ${dark ? 'bg-white/15' : 'bg-gray-200'}`}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${strength.percent}%`, backgroundColor: strength.color }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs font-medium" style={{ color: strength.color }} data-testid={`${testid}-label`}>
              {strength.label}
            </span>
            <span className={`text-xs ${dark ? 'text-slate-400' : 'text-gray-400'}`}>
              {checking ? <><i className="bi bi-arrow-repeat animate-spin"></i> vérification…</> : `${len} caractères`}
            </span>
          </div>
        </div>
      )}

      {/* Messages d'aide / d'alerte */}
      {tooShort && (
        <p className="text-xs text-[#DC2626] mt-1.5" data-testid={`${testid}-tooshort`}>
          <i className="bi bi-exclamation-circle mr-1"></i>
          Minimum {PASSWORD_MIN_LENGTH} caractères requis.
        </p>
      )}
      {!tooShort && pwnedCount > 0 && (
        <p className="text-xs text-[#DC2626] mt-1.5" data-testid={`${testid}-pwned`}>
          <i className="bi bi-shield-exclamation mr-1"></i>
          Ce mot de passe a été exposé dans une fuite de données ({pwnedCount.toLocaleString('fr-FR')} fois). Choisissez-en un autre.
        </p>
      )}
    </div>
  );
};

export default PasswordStrengthField;
